import React, { useState, useEffect } from 'react';
import type { Track } from '../../services/trackService';

interface TrackFormProps {
  initialData?: Partial<Track>;
  onSubmit: (trackData: Partial<Track>) => Promise<void>;
  onCancel: () => void;
  isEditing?: boolean;
}

/**
 * Form component for adding or editing a track
 */
const TrackForm: React.FC<TrackFormProps> = ({ 
  initialData = {}, 
  onSubmit, 
  onCancel,
  isEditing = false
}) => {
  const [formData, setFormData] = useState<Partial<Track>>({
    name: '',
    sku: '',
    costPrice: 0,
    salesPrice: 0,
    description: '',
    assignmentType: 'MANDATORY',
    ...initialData
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data when initialData changes
  useEffect(() => {
    setFormData({
      name: '',
      sku: '',
      costPrice: 0,
      salesPrice: 0,
      description: '',
      assignmentType: 'MANDATORY',
      ...initialData
    });
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    // Convert numeric values
    const parsedValue = type === 'number' ? parseFloat(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Required fields
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.sku?.trim()) {
      newErrors.sku = 'SKU is required';
    }
    
    if (!formData.costPrice || formData.costPrice <= 0) {
      newErrors.costPrice = 'Cost price must be a positive number';
    }
    
    if (!formData.salesPrice || formData.salesPrice <= 0) {
      newErrors.salesPrice = 'Sales price must be a positive number';
    }
    
    // Check if sales price is less than cost price (only if both are defined)
    if (formData.salesPrice !== undefined && 
        formData.costPrice !== undefined && 
        formData.salesPrice < formData.costPrice) {
      newErrors.salesPrice = 'Sales price should be greater than or equal to cost price';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSubmit(formData);
    } catch (error: any) {
      console.error('Error submitting track form:', error);
      
      // Handle API validation errors
      if (error?.response?.data?.error) {
        const errorMessage = error.response.data.error;
        if (typeof errorMessage === 'string') {
          if (errorMessage.includes('SKU already exists')) {
            setErrors(prev => ({
              ...prev,
              sku: 'This SKU already exists'
            }));
          } else if (errorMessage.includes('Track name already exists')) {
            setErrors(prev => ({
              ...prev,
              name: 'This track name already exists'
            }));
          } else {
            alert(`Error: ${errorMessage}`);
          }
        } else {
          alert('An error occurred while saving the track. Please try again.');
        }
      } else {
        alert('An error occurred while saving the track. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="track-form">
      <h2 className="text-xl font-bold mb-lg">
        {isEditing ? 'Edit Track' : 'Add New Track'}
      </h2>
      
      <div className="form-group mb-md">
        <label htmlFor="name" className="form-label">
          Track Name <span className="text-error">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-input ${errors.name ? 'border-error' : ''}`}
          placeholder="Enter track name"
        />
        {errors.name && <div className="text-error text-sm mt-xs">{errors.name}</div>}
      </div>
      
      <div className="form-group mb-md">
        <label htmlFor="sku" className="form-label">
          SKU <span className="text-error">*</span>
        </label>
        <input
          type="text"
          id="sku"
          name="sku"
          value={formData.sku}
          onChange={handleChange}
          className={`form-input ${errors.sku ? 'border-error' : ''}`}
          placeholder="Enter SKU (e.g., LEARN:GDPR)"
          disabled={isEditing} // SKU cannot be changed after creation
        />
        {errors.sku && <div className="text-error text-sm mt-xs">{errors.sku}</div>}
      </div>
      
      <div className="grid grid-cols-2 gap-md mb-md">
        <div className="form-group">
          <label htmlFor="costPrice" className="form-label">
            Cost Price <span className="text-error">*</span>
          </label>
          <input
            type="number"
            id="costPrice"
            name="costPrice"
            value={formData.costPrice}
            onChange={handleChange}
            className={`form-input ${errors.costPrice ? 'border-error' : ''}`}
            placeholder="0.00"
            min="0.01"
            step="0.01"
          />
          {errors.costPrice && <div className="text-error text-sm mt-xs">{errors.costPrice}</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="salesPrice" className="form-label">
            Sales Price <span className="text-error">*</span>
          </label>
          <input
            type="number"
            id="salesPrice"
            name="salesPrice"
            value={formData.salesPrice}
            onChange={handleChange}
            className={`form-input ${errors.salesPrice ? 'border-error' : ''}`}
            placeholder="0.00"
            min="0.01"
            step="0.01"
          />
          {errors.salesPrice && <div className="text-error text-sm mt-xs">{errors.salesPrice}</div>}
        </div>
      </div>
      
      <div className="form-group mb-md">
        <label htmlFor="description" className="form-label">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-input"
          placeholder="Enter track description"
          rows={3}
        />
      </div>
      
      <div className="form-group mb-lg">
        <label htmlFor="assignmentType" className="form-label">
          Assignment Type <span className="text-error">*</span>
        </label>
        <select
          id="assignmentType"
          name="assignmentType"
          value={formData.assignmentType}
          onChange={handleChange}
          className="form-input"
        >
          <option value="RANDOM">Random</option>
          <option value="ORDERED">Ordered</option>
          <option value="MANDATORY">Mandatory</option>
        </select>
        <div className="text-sm text-muted mt-xs">
          <strong>Random:</strong> Courses can be taken in any order<br />
          <strong>Ordered:</strong> Courses must be taken in a specific sequence<br />
          <strong>Mandatory:</strong> All courses must be assigned at once
        </div>
      </div>
      
      <div className="flex justify-end gap-md">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className="spinner-sm mr-xs"></div>
              {isEditing ? 'Saving...' : 'Creating...'}
            </>
          ) : (
            isEditing ? 'Save Changes' : 'Create Track'
          )}
        </button>
      </div>
    </form>
  );
};

export default TrackForm;
